import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Megaphone, Send, Users, Eye, Loader2, Calendar, Clock, Search, X, CheckCircle2, MailOpen, BellRing, Activity,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  'user', 'agent', 'agency_admin', 'developer',
  'sales_agent', 'sales_team_leader', 'sales_manager',
  'marketing_agent', 'marketing_team_leader', 'marketing_manager',
  'it_staff', 'accounting_staff', 'hr_staff',
  'quality_control_lead', 'quality_control_agent',
  'ad_operations_lead', 'ad_operations_agent',
];

type SegmentMode = 'all' | 'role' | 'city' | 'agency';
type SendMode = 'now' | 'schedule';
type StatusFilter = 'all' | 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';

interface BroadcastRow {
  id: string;
  title: string;
  body: string;
  status: string;
  recipient_count: number;
  channels: string[];
  segment: Record<string, any>;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  sent_by: string | null;
}

const segmentToText = (segment: Record<string, any> | null | undefined): string => {
  if (!segment) return '';
  if (segment.all) return 'all users';
  const parts: string[] = [];
  if (segment.role) parts.push(`role:${segment.role}`);
  if (segment.city) parts.push(`city:${segment.city}`);
  if (segment.agency_id) parts.push(`agency:${segment.agency_id}`);
  return parts.join(' ');
};

export function BroadcastCenter() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [emailChecked, setEmailChecked] = useState(false);
  const [segmentMode, setSegmentMode] = useState<SegmentMode>('all');
  const [selectedRole, setSelectedRole] = useState<string>('user');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedAgencyId, setSelectedAgencyId] = useState('');

  const [sendMode, setSendMode] = useState<SendMode>('now');
  const [scheduledAt, setScheduledAt] = useState('');

  const [previewing, setPreviewing] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [sending, setSending] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Detail drawer
  const [detailId, setDetailId] = useState<string | null>(null);

  // Cancel confirmation
  const [cancelTarget, setCancelTarget] = useState<BroadcastRow | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const { data: broadcasts, isLoading } = useQuery({
    queryKey: ['broadcasts-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('broadcasts' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as BroadcastRow[];
    },
    refetchInterval: 30_000,
  });

  const { data: agencies } = useQuery({
    queryKey: ['agencies-for-broadcast'],
    queryFn: async () => {
      const { data } = await supabase.from('agencies').select('id, name, city').order('name');
      return data ?? [];
    },
  });

  // Last dispatch run from admin_actions
  const { data: lastRun } = useQuery({
    queryKey: ['broadcast-dispatch-last-run'],
    queryFn: async () => {
      const { data } = await supabase
        .from('admin_actions')
        .select('action, metadata, created_at')
        .eq('entity_type', 'broadcast_dispatcher')
        .eq('action', 'broadcast_dispatch_finished')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    refetchInterval: 30_000,
  });

  const filteredBroadcasts = useMemo(() => {
    const list = broadcasts ?? [];
    const q = searchTerm.trim().toLowerCase();
    return list.filter((b) => {
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (!q) return true;
      const haystack = `${b.title} ${b.body} ${segmentToText(b.segment)}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [broadcasts, statusFilter, searchTerm]);

  const buildSegment = () => {
    if (segmentMode === 'all') return { all: true };
    if (segmentMode === 'role') return { role: selectedRole };
    if (segmentMode === 'city') return { city: selectedCity };
    if (segmentMode === 'agency') return { agency_id: selectedAgencyId };
    return { all: true };
  };

  const resetForm = () => {
    setTitle(''); setBody(''); setEmailChecked(false);
    setSegmentMode('all'); setSelectedRole('user');
    setSelectedCity(''); setSelectedAgencyId('');
    setPreviewCount(null);
    setSendMode('now'); setScheduledAt('');
  };

  const callBroadcastFn = async (preview: boolean, schedule = false) => {
    const segment = buildSegment();
    const scheduledIso = schedule && scheduledAt ? new Date(scheduledAt).toISOString() : null;
    const { data, error } = await supabase.functions.invoke('broadcasts', {
      body: {
        title: title || 'Untitled',
        body: body || ' ',
        channels: emailChecked ? ['in_app', 'email'] : ['in_app'],
        segment,
        preview_only: preview,
        scheduled_at: scheduledIso,
      },
    });
    if (error) throw error;
    return data as {
      count?: number;
      recipient_count?: number;
      estimated_recipient_count?: number;
      broadcast_id?: string;
      scheduled_at?: string;
    };
  };

  const handlePreview = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setPreviewing(true);
    try {
      const r = await callBroadcastFn(true);
      setPreviewCount(r.count ?? 0);
    } catch (e: any) {
      toast.error(e.message || 'Failed to preview audience');
    } finally {
      setPreviewing(false);
    }
  };

  const validateSchedule = () => {
    if (sendMode !== 'schedule') return true;
    if (!scheduledAt) {
      toast.error('Pick a date and time for the scheduled send');
      return false;
    }
    const t = new Date(scheduledAt).getTime();
    if (isNaN(t)) {
      toast.error('Invalid scheduled date');
      return false;
    }
    if (t < Date.now() + 30_000) {
      toast.error('Scheduled time must be at least 30 seconds in the future');
      return false;
    }
    return true;
  };

  const handleSendClick = () => {
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (previewCount === null) {
      toast.info('Click Preview audience first to confirm recipients');
      return;
    }
    if (!validateSchedule()) return;
    setConfirmOpen(true);
  };

  const handleConfirmSend = async () => {
    setSending(true);
    try {
      const r = await callBroadcastFn(false, sendMode === 'schedule');
      if (sendMode === 'schedule') {
        toast.success(
          `Broadcast scheduled for ${new Date(r.scheduled_at!).toLocaleString()} (~${r.estimated_recipient_count ?? 0} recipients)`,
        );
      } else {
        toast.success(`Broadcast sent to ${r.recipient_count ?? 0} recipients`);
      }
      setConfirmOpen(false);
      setOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['broadcasts-list'] });
    } catch (e: any) {
      toast.error(e.message || 'Failed to send broadcast');
    } finally {
      setSending(false);
    }
  };

  const handleCancelSchedule = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { error } = await supabase
        .from('broadcasts' as any)
        .update({ status: 'draft', scheduled_at: null })
        .eq('id', cancelTarget.id)
        .eq('status', 'scheduled'); // safety: only cancel if still scheduled
      if (error) throw error;

      // Audit log (best effort)
      const { data: auth } = await supabase.auth.getUser();
      if (auth.user) {
        await supabase.from('admin_actions').insert({
          admin_id: auth.user.id,
          action: 'broadcast_cancel_schedule',
          entity_type: 'broadcast',
          entity_id: cancelTarget.id,
          metadata: {
            title: cancelTarget.title,
            previous_scheduled_at: cancelTarget.scheduled_at,
          },
        });
      }

      toast.success('Scheduled broadcast cancelled');
      setCancelTarget(null);
      queryClient.invalidateQueries({ queryKey: ['broadcasts-list'] });
    } catch (e: any) {
      toast.error(e.message || 'Failed to cancel scheduled broadcast');
    } finally {
      setCancelling(false);
    }
  };

  const defaultScheduleValue = () => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const statusVariant = (s: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
    if (s === 'sent') return 'default';
    if (s === 'scheduled') return 'outline';
    if (s === 'sending') return 'secondary';
    if (s === 'failed') return 'destructive';
    return 'secondary';
  };

  const lastRunDisplay = useMemo(() => {
    if (!lastRun?.created_at) return 'Never';
    const meta = (lastRun.metadata ?? {}) as Record<string, any>;
    const when = new Date(lastRun.created_at).toLocaleString();
    const processed = typeof meta.processed === 'number' ? meta.processed : 0;
    const failed = typeof meta.failed === 'number' ? meta.failed : 0;
    return `${when} · ${processed} processed${failed ? `, ${failed} failed` : ''}`;
  }, [lastRun]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-primary" /> Broadcast Center
          </h1>
          <p className="text-sm text-muted-foreground">Send in-app announcements now or schedule them for later.</p>
        </div>

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button><Send className="h-4 w-4 mr-2" /> New Broadcast</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Compose broadcast</DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="b-title">Title</Label>
                <Input id="b-title" value={title} onChange={(e) => { setTitle(e.target.value); setPreviewCount(null); }} placeholder="e.g. Platform maintenance window" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="b-body">Message</Label>
                <Textarea id="b-body" value={body} onChange={(e) => { setBody(e.target.value); setPreviewCount(null); }} rows={5} placeholder="What do you want to tell your users?" />
              </div>

              <div className="space-y-2">
                <Label>Channels</Label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked disabled /> In-App (always on)
                  </label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <label className="flex items-center gap-2 text-sm opacity-60 cursor-not-allowed">
                          <Checkbox checked={emailChecked} disabled onCheckedChange={(v) => setEmailChecked(!!v)} />
                          Email
                        </label>
                      </TooltipTrigger>
                      <TooltipContent>Email broadcasts coming soon — SMTP not configured.</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Audience</Label>
                <Select value={segmentMode} onValueChange={(v) => { setSegmentMode(v as SegmentMode); setPreviewCount(null); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    <SelectItem value="role">By role</SelectItem>
                    <SelectItem value="city">By city</SelectItem>
                    <SelectItem value="agency">By agency</SelectItem>
                  </SelectContent>
                </Select>

                {segmentMode === 'role' && (
                  <Select value={selectedRole} onValueChange={(v) => { setSelectedRole(v); setPreviewCount(null); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {segmentMode === 'city' && (
                  <Input value={selectedCity} onChange={(e) => { setSelectedCity(e.target.value); setPreviewCount(null); }} placeholder="e.g. Addis Ababa" />
                )}

                {segmentMode === 'agency' && (
                  <Select value={selectedAgencyId} onValueChange={(v) => { setSelectedAgencyId(v); setPreviewCount(null); }}>
                    <SelectTrigger><SelectValue placeholder="Pick an agency" /></SelectTrigger>
                    <SelectContent>
                      {(agencies ?? []).map((a: any) => (
                        <SelectItem key={a.id} value={a.id}>{a.name} {a.city ? `· ${a.city}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label>When to send</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={sendMode === 'now' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSendMode('now')}
                  >
                    <Send className="h-3.5 w-3.5 mr-1.5" /> Now
                  </Button>
                  <Button
                    type="button"
                    variant={sendMode === 'schedule' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setSendMode('schedule');
                      if (!scheduledAt) setScheduledAt(defaultScheduleValue());
                    }}
                  >
                    <Calendar className="h-3.5 w-3.5 mr-1.5" /> Schedule
                  </Button>
                </div>

                {sendMode === 'schedule' && (
                  <div className="space-y-1.5 pt-1">
                    <Input
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      min={defaultScheduleValue()}
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Will auto-send within ~1 minute of the scheduled time. Audience is re-resolved at send time.
                    </p>
                  </div>
                )}
              </div>

              {previewCount !== null && (
                <div className="rounded-md bg-muted/50 p-3 text-sm flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span>Audience size: <strong>{previewCount}</strong> recipient{previewCount === 1 ? '' : 's'}</span>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={handlePreview} disabled={previewing}>
                {previewing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Preview audience
              </Button>
              <Button onClick={handleSendClick} disabled={sending}>
                {sendMode === 'schedule' ? (
                  <><Calendar className="h-4 w-4 mr-2" /> Schedule</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Send now</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Confirmation modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{sendMode === 'schedule' ? 'Schedule broadcast?' : 'Send broadcast?'}</DialogTitle>
          </DialogHeader>
          {sendMode === 'schedule' ? (
            <p className="text-sm">
              Schedule an in-app notification to approximately <strong>{previewCount ?? 0}</strong> recipient{previewCount === 1 ? '' : 's'} for{' '}
              <strong>{scheduledAt ? new Date(scheduledAt).toLocaleString() : '—'}</strong>.
              The actual audience is re-resolved when the broadcast fires.
            </p>
          ) : (
            <p className="text-sm">
              This will deliver an in-app notification to <strong>{previewCount ?? 0}</strong> recipient{previewCount === 1 ? '' : 's'}. This action cannot be undone.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmSend} disabled={sending}>
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : (sendMode === 'schedule' ? <Calendar className="h-4 w-4 mr-2" /> : <Send className="h-4 w-4 mr-2" />)}
              {sendMode === 'schedule' ? 'Confirm schedule' : 'Confirm send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel schedule confirmation */}
      <Dialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel scheduled broadcast?</DialogTitle>
            <DialogDescription>
              <strong>{cancelTarget?.title}</strong> is set to dispatch at{' '}
              <strong>{cancelTarget?.scheduled_at ? new Date(cancelTarget.scheduled_at).toLocaleString() : '—'}</strong>.
              Cancelling will move it back to draft so it won&apos;t be sent automatically.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelTarget(null)} disabled={cancelling}>Keep schedule</Button>
            <Button variant="destructive" onClick={handleCancelSchedule} disabled={cancelling}>
              {cancelling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <X className="h-4 w-4 mr-2" />}
              Cancel schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatcher status pill */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Activity className="h-3.5 w-3.5" />
        <span>Last dispatch run: <strong className="text-foreground">{lastRunDisplay}</strong></span>
      </div>

      {/* Past + scheduled broadcasts */}
      <Card className="p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Broadcasts</h2>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search title, body, segment…"
                className="pl-7 h-8 w-56 text-xs"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
              <SelectTrigger className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="sending">Sending</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            {(searchTerm || statusFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs"
                onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
              >
                <X className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 rounded-md" />)}
          </div>
        ) : filteredBroadcasts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            {(broadcasts ?? []).length === 0
              ? 'No broadcasts yet — compose one above.'
              : 'No broadcasts match the current filters.'}
          </p>
        ) : (
          <div className="divide-y">
            {filteredBroadcasts.map((b) => (
              <div key={b.id} className="py-3 flex items-start gap-4 flex-wrap">
                <button
                  type="button"
                  onClick={() => setDetailId(b.id)}
                  className="flex-1 min-w-[200px] text-left hover:opacity-80 transition-opacity"
                >
                  <div className="font-medium text-sm">{b.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">{b.body}</div>
                  {b.segment && (
                    <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                      {segmentToText(b.segment) || '—'}
                    </div>
                  )}
                </button>
                <Badge variant={statusVariant(b.status)} className="capitalize">{b.status}</Badge>
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Users className="h-3 w-3" /> {b.recipient_count}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  {b.status === 'scheduled' ? (
                    <><Clock className="h-3 w-3" /> {b.scheduled_at ? new Date(b.scheduled_at).toLocaleString() : '—'}</>
                  ) : (
                    <>{b.sent_at ? new Date(b.sent_at).toLocaleString() : '—'}</>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setDetailId(b.id)}>
                    <Eye className="h-3 w-3 mr-1" /> Details
                  </Button>
                  {b.status === 'scheduled' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive"
                      onClick={() => setCancelTarget(b)}
                    >
                      <X className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <BroadcastDetailDrawer
        broadcastId={detailId}
        broadcast={(broadcasts ?? []).find((b) => b.id === detailId) ?? null}
        onClose={() => setDetailId(null)}
        lastRunDisplay={lastRunDisplay}
      />
    </div>
  );
}

interface DrawerProps {
  broadcastId: string | null;
  broadcast: BroadcastRow | null;
  onClose: () => void;
  lastRunDisplay: string;
}

function BroadcastDetailDrawer({ broadcastId, broadcast, onClose, lastRunDisplay }: DrawerProps) {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['broadcast-recipient-stats', broadcastId],
    enabled: !!broadcastId,
    queryFn: async () => {
      if (!broadcastId) return null;
      // Pull all recipient rows. Recipients table is small per-broadcast; fine for typical sizes.
      const { data, error } = await supabase
        .from('broadcast_recipients' as any)
        .select('id, delivered_at, read_at')
        .eq('broadcast_id', broadcastId);
      if (error) throw error;
      const rows = ((data ?? []) as unknown) as Array<{ id: string; delivered_at: string | null; read_at: string | null }>;
      const delivered = rows.filter((r) => !!r.delivered_at).length;
      const read = rows.filter((r) => !!r.read_at).length;
      const total = rows.length;
      const undelivered = Math.max(0, total - delivered);
      const unread = Math.max(0, delivered - read);
      return { total, delivered, read, undelivered, unread };
    },
  });

  const open = !!broadcastId && !!broadcast;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-primary" />
            {broadcast?.title || 'Broadcast'}
          </SheetTitle>
          <SheetDescription>
            <span className="capitalize">{broadcast?.status}</span>
            {broadcast?.scheduled_at && (
              <> · scheduled {new Date(broadcast.scheduled_at).toLocaleString()}</>
            )}
            {broadcast?.sent_at && (
              <> · sent {new Date(broadcast.sent_at).toLocaleString()}</>
            )}
          </SheetDescription>
        </SheetHeader>

        {broadcast && (
          <div className="mt-6 space-y-6">
            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Message</h3>
              <p className="text-sm whitespace-pre-wrap rounded-md bg-muted/40 p-3 border">{broadcast.body}</p>
            </section>

            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Audience</h3>
              <div className="text-sm space-y-1">
                <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5 text-muted-foreground" /> Estimated/recorded: <strong>{broadcast.recipient_count}</strong></div>
                <div className="text-xs text-muted-foreground">Segment: {segmentToText(broadcast.segment) || '—'}</div>
                <div className="text-xs text-muted-foreground">Channels: {(broadcast.channels ?? []).join(', ') || '—'}</div>
              </div>
            </section>

            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Delivery</h3>
              {isLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-14 rounded-md" />
                  <Skeleton className="h-14 rounded-md" />
                </div>
              ) : !stats || stats.total === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No recipient rows yet{broadcast.status === 'scheduled' ? ' — will be created when the broadcast dispatches.' : '.'}
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <StatTile icon={<BellRing className="h-4 w-4" />} label="Total" value={stats.total} />
                  <StatTile icon={<CheckCircle2 className="h-4 w-4 text-primary" />} label="Delivered" value={stats.delivered} />
                  <StatTile icon={<MailOpen className="h-4 w-4 text-primary" />} label="Read" value={stats.read} />
                  <StatTile icon={<Clock className="h-4 w-4 text-muted-foreground" />} label="Unread" value={stats.unread} />
                  {stats.undelivered > 0 && (
                    <StatTile icon={<X className="h-4 w-4 text-destructive" />} label="Undelivered" value={stats.undelivered} />
                  )}
                </div>
              )}
              {stats && stats.total > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                    <span>Read rate</span>
                    <span>{Math.round((stats.read / stats.total) * 100)}%</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${(stats.read / stats.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </section>

            <section>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Dispatcher</h3>
              <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Activity className="h-3.5 w-3.5" />
                Last run: <span className="text-foreground">{lastRunDisplay}</span>
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StatTile({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="text-lg font-bold mt-0.5">{value}</div>
    </div>
  );
}
